import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CustomInput from '../../components/CustomInput';
import CustomModal from '../../components/CustomModal';
import CustomTable from '../../components/CustomTable/CustomTable';
import Title from '../../components/Title';
import {
    allProducts,
    productsLoadingStatus,
    getProducts,
    createProduct,
    editProduct,
    deleteProduct,
} from '../../redux/slices/productsSlice';
import moment from 'moment';
import { Helmet } from 'react-helmet';
import { toast } from 'react-toastify';
import { Space } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';

export default function ProductsPage() {
    const dispatch = useDispatch();

    const products = useSelector(allProducts);
    const productsStatus = useSelector(productsLoadingStatus);

    const [isAddProductModalVisible, setIsAddProductModalVisible] =
        useState(false);
    const [isEditProductModalVisible, setIsEditProductModalVisible] =
        useState(false);
    const [isDeleteProductModalVisible, setIsDeleteProductModalVisible] =
        useState(false);
    const [productName, setProductName] = useState('');
    const [productCode, setProductCode] = useState('');
    const [productId, setProductId] = useState(null);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        dispatch(getProducts());
    }, [dispatch]);

    const toggleAddProductModal = () => {
        setProductName('');
        setProductCode('');
        setErrors({});
        setIsAddProductModalVisible(!isAddProductModalVisible);
    };

    const toggleEditProductModal = (record) => {
        if (record === undefined) {
            setIsEditProductModalVisible(!isEditProductModalVisible);
        } else {
            setProductName(record.name);
            setProductCode(record.code);
            setProductId(record.id);
            setErrors({});
            setIsEditProductModalVisible(!isEditProductModalVisible);
        }
    };

    const toggleDeleteProductModal = (record) => {
        if (record === undefined) {
            setIsDeleteProductModalVisible(!isDeleteProductModalVisible);
        } else {
            setProductName(record.name);
            setProductId(record.id);
            setIsDeleteProductModalVisible(!isDeleteProductModalVisible);
        }
    };

    const onInputChange = (e) => {
        if (e.target.id === 'name') setProductName(e.target.value);
        if (e.target.id === 'code') setProductCode(e.target.value);
    };

    const onAddProduct = async () => {
        try {
            await dispatch(
                createProduct({ name: productName, code: productCode }),
            ).unwrap();
            toast.success('New product successfully added');
            toggleAddProductModal();
            dispatch(getProducts());
        } catch (error) {
            setErrors(error.errors);
        }
    };

    const onEditProduct = async () => {
        const data = {
            id: productId,
            body: { name: productName, code: productCode },
        };

        try {
            await dispatch(editProduct(data)).unwrap();
            toast.success('Changes saved successfully');
            toggleEditProductModal();
            dispatch(getProducts());
        } catch (error) {
            setErrors(error.errors);
        }
    };

    const onDeleteProduct = async () => {
        try {
            await dispatch(deleteProduct(productId)).unwrap();
            toast.success('Product deleted successfully');
            toggleDeleteProductModal();
            dispatch(getProducts());
        } catch (error) {
            setErrors(error.errors);
        }
    };

    const getFilteredProducts = (data, filter) => {
        const formatedData = data?.map((el) => {
            const newObj = { ...el };
            if (newObj.created_at === null) {
                newObj.created_at = '';
            } else {
                newObj.created_at = moment(el.created_at).format(
                    'YYYY-MM-DD HH:mm',
                );
            }
            return newObj;
        });
        const filteredData = formatedData.filter((el) =>
            el?.name.toLowerCase().includes(filter.toLowerCase()),
        );

        return filteredData;
    };

    const actionsOption = {
        title: 'Actions',
        render: (_, record) => (
            <Space size="middle">
                <EditOutlined
                    onClick={(e) => toggleEditProductModal(record, e)}
                />
                <DeleteOutlined
                    onClick={(e) => toggleDeleteProductModal(record, e)}
                />
            </Space>
        ),
    };

    return (
        <div>
            <Helmet>
                <title>Console Defentry - Products</title>
            </Helmet>
            <Title>Products</Title>
            <div className="table-wrapper">
                <CustomTable
                    tableHeader={['ID', 'Name', 'Code', 'Actions']}
                    functionalCell={{ Actions: actionsOption }}
                    onAddButtonClick={toggleAddProductModal}
                    tablePannelTitle="Table of products"
                    searchInputPlaceholder="Search by name"
                    topPannelButtonText="Add product"
                    data={products}
                    getFilteredData={getFilteredProducts}
                    isLoading={productsStatus === 'loading'}
                    withFilter
                />
            </div>
            <CustomModal
                title="Add new product"
                subText="Please enter product details"
                subTextAlign="left"
                isModalVisible={isAddProductModalVisible}
                onAcceptButtonClick={onAddProduct}
                onCancelButtonClick={toggleAddProductModal}
                cancelButtonText="Cancel"
                acceptButtonText="Add"
            >
                <CustomInput
                    id="name"
                    placeholder="Enter product name"
                    onInputChange={onInputChange}
                    inputValue={productName}
                    labelText="Name"
                    width="100%"
                    errorText={errors?.name}
                />
                <div style={{ marginTop: '16px' }} />
                <CustomInput
                    id="code"
                    placeholder="Enter product code"
                    onInputChange={onInputChange}
                    inputValue={productCode}
                    labelText="Product code"
                    width="100%"
                    errorText={errors?.code}
                />
            </CustomModal>
            <CustomModal
                title="Edit product"
                subText="Edit product name below"
                subTextAlign="left"
                isModalVisible={isEditProductModalVisible}
                onAcceptButtonClick={onEditProduct}
                onCancelButtonClick={toggleEditProductModal}
                cancelButtonText="Cancel"
                acceptButtonText="Save"
            >
                <CustomInput
                    id="name"
                    placeholder="Enter product name"
                    onInputChange={onInputChange}
                    inputValue={productName}
                    labelText="Name"
                    width="100%"
                    errorText={errors?.name}
                />
                <div style={{ marginTop: '16px' }} />
                <CustomInput
                    id="code"
                    placeholder="Enter product code"
                    onInputChange={onInputChange}
                    inputValue={productCode}
                    labelText="Product code"
                    width="100%"
                    errorText={errors?.code}
                />
            </CustomModal>
            <CustomModal
                title={
                    <p>
                        Are you sure you want to delete
                        <span className="highlighted-text">{` ${productName} `}</span>
                        product?
                    </p>
                }
                subText="You will delete this product without recovery"
                subTextAlign="center"
                isModalVisible={isDeleteProductModalVisible}
                onAcceptButtonClick={onDeleteProduct}
                onCancelButtonClick={toggleDeleteProductModal}
                cancelButtonText="Cancel"
                acceptButtonText="Delete"
            ></CustomModal>
        </div>
    );
}
